import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import ConfirmDialog from "@/pages/admin/dashboard/components/ConfirmDialog";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type { AdminProfile, Role, AccountType } from "@/pages/admin/dashboard/types";

interface UsersTabProps {
  profiles: AdminProfile[];
  currentUserId?: string;
  onRefresh: () => void;
}

const ROLE_META: Record<Role, { label: string; icon: string; badge: string }> = {
  user: {
    label: "Usuario",
    icon: "ri-user-line",
    badge: "bg-background-100 text-foreground-600",
  },
  admin: {
    label: "Administrador",
    icon: "ri-shield-star-line",
    badge: "bg-accent-100 text-accent-800",
  },
  super_admin: {
    label: "Super Admin",
    icon: "ri-vip-crown-line",
    badge: "bg-primary-100 text-primary-700",
  },
};

const ACCOUNT_META: Record<AccountType, string> = {
  buyer: "Comprador",
  seller: "Vendedor",
};

function initials(name: string | null, email: string | null): string {
  return (name ?? email ?? "U").charAt(0).toUpperCase();
}

export default function UsersTab({
  profiles,
  currentUserId,
  onRefresh,
}: UsersTabProps) {
  const { showToast } = useToast();
  const [suspendTarget, setSuspendTarget] = useState<AdminProfile | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const sorted = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [profiles]
  );

  const requestSuspend = (target: AdminProfile) => {
    if (target.id === currentUserId) {
      showToast("No puedes suspender tu propia cuenta.", "info");
      return;
    }
    if (target.role === "super_admin") {
      showToast("No puedes bloquear al super administrador.", "info");
      return;
    }
    setSuspendTarget(target);
  };

  const confirmSuspend = async () => {
    if (!suspendTarget) return;
    const isBlocked = !suspendTarget.is_active;
    setBusyId(suspendTarget.id);
    // Las operaciones sensibles pasan por RPC seguras (SECURITY DEFINER),
    // que validan permisos en PostgreSQL.
    const rpcName = isBlocked ? "unblock_user" : "block_user";
    const { error } = await supabase.rpc(rpcName, {
      target_uuid: suspendTarget.id,
      ...(isBlocked ? {} : { reason: "Suspensión administrativa" }),
    });
    setBusyId(null);
    setSuspendTarget(null);
    if (error) {
      showToast(error.message || "No se pudo actualizar la cuenta.", "error");
      return;
    }
    showToast(
      isBlocked ? "Cuenta reactivada." : "Cuenta suspendida.",
      "success"
    );
    onRefresh();
  };

  if (profiles.length === 0) {
    return (
      <EmptyState
        icon="ri-team-line"
        title="Sin usuarios"
        description="Aún no hay usuarios registrados en la plataforma."
      />
    );
  }

  const isBlockedTarget = !suspendTarget?.is_active;

  return (
    <>
      {/* Tabla (escritorio) */}
      <div className="hidden md:block">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-100/60 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              <th className="px-5 py-3">Usuario</th>
              <th className="px-5 py-3">Tipo</th>
              <th className="px-5 py-3">Rol</th>
              <th className="px-5 py-3">Estado</th>
              <th className="px-5 py-3">Registro</th>
              <th className="px-5 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const meta = ROLE_META[p.role];
              return (
                <tr
                  key={p.id}
                  className="border-b border-background-200/60 transition-colors last:border-0 hover:bg-background-100/40"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                        {initials(p.full_name, p.email)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground-950">
                          {p.full_name ?? "Usuario"}
                          {p.id === currentUserId && (
                            <span className="ml-1.5 text-xs font-normal text-foreground-400">
                              (tú)
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-foreground-500">
                          {p.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-700">
                      <i
                        className={`${
                          p.account_type === "seller"
                            ? "ri-store-2-line"
                            : "ri-shopping-bag-3-line"
                        } text-base text-foreground-400`}
                      ></i>
                      {ACCOUNT_META[p.account_type]}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
                    >
                      <i className={`${meta.icon} text-sm`}></i>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {!p.is_active ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                        <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                        Suspendido
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-secondary-500"></span>
                        Activo
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-foreground-600">
                    {timeAgo(p.created_at)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      disabled={p.id === currentUserId || p.role === "super_admin"}
                      onClick={() => requestSuspend(p)}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <i
                        className={`${
                          !p.is_active ? "ri-user-received-line" : "ri-user-forbid-line"
                        } text-sm`}
                      ></i>
                      {!p.is_active ? "Reactivar" : "Suspender"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tarjetas (móvil) */}
      <div className="divide-y divide-background-200/60 md:hidden">
        {sorted.map((p) => {
          const meta = ROLE_META[p.role];
          return (
            <div key={p.id} className="p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                  {initials(p.full_name, p.email)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">
                    {p.full_name ?? "Usuario"}
                  </p>
                  <p className="truncate text-xs text-foreground-500">{p.email}</p>
                </div>
                {!p.is_active ? (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                    Suspendido
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700">
                    Activo
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
                >
                  <i className={`${meta.icon} text-sm`}></i>
                  {meta.label}
                </span>
                <span className="text-xs text-foreground-500">
                  {ACCOUNT_META[p.account_type]}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-foreground-400">
                  Registrado {timeAgo(p.created_at)}
                </p>
                <button
                  type="button"
                  disabled={p.id === currentUserId || p.role === "super_admin"}
                  onClick={() => requestSuspend(p)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-accent-200 px-3 py-1.5 text-xs font-medium text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {!p.is_active ? "Reactivar" : "Suspender"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={suspendTarget !== null}
        title={isBlockedTarget ? "¿Reactivar esta cuenta?" : "¿Suspender esta cuenta?"}
        description={
          isBlockedTarget
            ? `${
                suspendTarget?.full_name ?? "El usuario"
              } podrá volver a iniciar sesión y usar la plataforma.`
            : `${
                suspendTarget?.full_name ?? "El usuario"
              } dejará de poder iniciar sesión y usar la plataforma.`
        }
        confirmLabel={isBlockedTarget ? "Reactivar" : "Suspender"}
        tone={isBlockedTarget ? "primary" : "danger"}
        loading={busyId === suspendTarget?.id}
        onConfirm={confirmSuspend}
        onCancel={() => setSuspendTarget(null)}
      />
    </>
  );
}